/**
 * Chatwoot live-chat integration (replaces Crisp).
 *
 * - Loads the website SDK (bottom-right widget).
 * - Identifies logged-in users with HMAC identity validation.
 *   The HMAC is generated server-side; the widget only receives identifier_hash.
 * - Hidden on mobile; the More menu "Support" item opens the widget.
 *
 * @see https://www.chatwoot.com/hc/user-guide/articles/1677587234-how-to-send-additional-user-information-to-chatwoot-using-sdk
 * @see https://www.chatwoot.com/hc/user-guide/articles/1677587479-how-to-enable-identity-validation-in-chatwoot
 */

import axios from 'axios';

const DEFAULT_WEBSITE_TOKEN = 'kZyhgnC4g2xQBneHsVwrjX9v';
const DEFAULT_BASE_URL = 'https://support.codigital.nl';

// Matches the `lg-down` breakpoint used across the dashboard SCSS.
const MOBILE_MEDIA_QUERY = '(max-width: 1023px)';

let mobileListenerRegistered = false;
let chatwootReady = false;
let pendingIdentity = null;
let identifyRequestId = 0;

function websiteToken() {
  return import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN || DEFAULT_WEBSITE_TOKEN;
}

function baseUrl() {
  return import.meta.env.VITE_CHATWOOT_BASE_URL || DEFAULT_BASE_URL;
}

function isEnabledByEnv() {
  return import.meta.env.PROD || import.meta.env.VITE_CHATWOOT_ENABLED === 'true';
}

function sdkReady() {
  return typeof window !== 'undefined' && Boolean(window.$chatwoot);
}

function isMobileViewport() {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia(MOBILE_MEDIA_QUERY).matches
    : false;
}

function applyMobileVisibility() {
  if (!sdkReady()) return;
  window.$chatwoot.toggleBubbleVisibility(isMobileViewport() ? 'hide' : 'show');
}

function setupMobileVisibility() {
  if (typeof window === 'undefined' || !window.matchMedia) return;

  applyMobileVisibility();

  if (mobileListenerRegistered) return;
  mobileListenerRegistered = true;

  const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
  const handler = () => applyMobileVisibility();
  if (mql.addEventListener) mql.addEventListener('change', handler);
  else if (mql.addListener) mql.addListener(handler);

  window.addEventListener('chatwoot:closed', () => {
    if (isMobileViewport() && sdkReady()) {
      window.$chatwoot.toggleBubbleVisibility('hide');
    }
  });
}

function applyIdentity(identity) {
  if (!sdkReady() || !identity?.identifierHash || !identity?.userId) return;

  try {
    window.$chatwoot.setUser(String(identity.userId), {
      email: identity.email || '',
      name: identity.nickname || '',
      avatar_url: identity.avatarUrl || '',
      identifier_hash: identity.identifierHash,
      company_name: identity.organizationName || '',
    });

    const attrs = {};
    if (identity.organizationId) attrs.organization_id = identity.organizationId;
    if (identity.organizationName) attrs.organization_name = identity.organizationName;
    if (Object.keys(attrs).length) {
      window.$chatwoot.setCustomAttributes(attrs);
    }
  } catch (err) {
    console.warn('[Chatwoot] Failed to identify user:', err);
  }
}

/**
 * Inject the Chatwoot website SDK. Safe to call multiple times — only loads once.
 */
export function initChatwoot() {
  if (!isEnabledByEnv()) {
    if (import.meta.env.DEV) {
      console.log('[Chatwoot] Disabled in development (set VITE_CHATWOOT_ENABLED=true to enable)');
    }
    return;
  }

  const token = websiteToken();
  if (!token) {
    console.warn('[Chatwoot] No website token configured, skipping initialization');
    return;
  }

  if (typeof window === 'undefined') return;
  if (window.__chatwootLoaded) return;
  if (document.querySelector('script[src*="/packs/js/sdk.js"]')) {
    window.__chatwootLoaded = true;
    return;
  }

  try {
    window.__chatwootLoaded = true;
    window.chatwootSettings = {
      position: 'right',
      type: 'standard',
      launcherTitle: '',
      locale: 'nl',
      hideMessageBubble: isMobileViewport(),
    };

    window.addEventListener('chatwoot:ready', () => {
      chatwootReady = true;
      setupMobileVisibility();
      if (pendingIdentity) applyIdentity(pendingIdentity);
    });

    (function (d, t) {
      const BASE_URL = baseUrl();
      const g = d.createElement(t);
      const s = d.getElementsByTagName(t)[0];
      g.src = BASE_URL + '/packs/js/sdk.js';
      g.async = true;
      s.parentNode.insertBefore(g, s);
      g.onload = function () {
        window.chatwootSDK.run({
          websiteToken: token,
          baseUrl: BASE_URL,
        });
      };
    })(document, 'script');
  } catch (err) {
    window.__chatwootLoaded = false;
    console.warn('[Chatwoot] Failed to load:', err);
  }
}

/**
 * Identify the current user. Fetches identifier_hash from the API (HMAC stays server-side).
 * Safe to call before the SDK is ready — applied on `chatwoot:ready`.
 */
export async function identifyChatwootUser({
  email,
  nickname,
  userId,
  avatarUrl,
  organizationName,
  organizationId,
} = {}) {
  if (!isChatwootEnabled() || !userId) return;

  const requestId = ++identifyRequestId;
  pendingIdentity = {
    email,
    nickname,
    userId,
    avatarUrl,
    organizationName,
    organizationId,
  };

  try {
    const { data } = await axios.get('/users/me/chatwoot');
    if (requestId !== identifyRequestId) return;

    pendingIdentity = {
      ...pendingIdentity,
      userId: data.identifier,
      identifierHash: data.identifier_hash,
    };

    if (chatwootReady) applyIdentity(pendingIdentity);
  } catch (err) {
    if (requestId !== identifyRequestId) return;
    console.warn('[Chatwoot] Failed to fetch identity hash:', err);
  }
}

/**
 * Reset the Chatwoot session (call on logout so the next user starts fresh).
 * Do not call this from `chatwoot:ready` — reset re-emits that event.
 */
export function resetChatwootSession() {
  identifyRequestId += 1;
  pendingIdentity = null;
  if (!sdkReady()) return;
  try {
    window.$chatwoot.reset();
  } catch (err) {
    console.warn('[Chatwoot] Failed to reset session:', err);
  }
}

/**
 * Programmatically open the Chatwoot chat window.
 * Used from the mobile "More" menu since the default launcher is hidden on mobile.
 */
export function openChatwootChat() {
  if (!sdkReady()) return;
  try {
    window.$chatwoot.toggle('open');
  } catch (err) {
    console.warn('[Chatwoot] Failed to open chat:', err);
  }
}

/**
 * Whether Chatwoot is configured to load (helps templates know if a "Support" entry should appear).
 */
export function isChatwootEnabled() {
  return Boolean(isEnabledByEnv() && websiteToken());
}
